import React from 'react';
import { X, Download } from 'lucide-react';

interface BlogType {
  id: number;
  title: string;
  file_name: string;
  topic: string;
  status: string;
  folder_id: number;
  created_at: string;
  scheduled_at?: string;
  cover_img_url: string;
}

interface PublishedBlogsTableProps {
  blogs: BlogType[];
  handleDeleteBlog: (blogId: number) => void;
  handlePublishBlog?: (blogId: number) => void;
  getStatusColor: (status: string) => string;
  handleDownloadFile?: (blogId: number, fileName: string) => void;
}

const PublishedBlogsTable: React.FC<PublishedBlogsTableProps> = ({
  blogs,
  handleDeleteBlog,
  handlePublishBlog,
  getStatusColor,
  handleDownloadFile,
}) => {
  if (blogs.length === 0) {
    return (
      <div className="text-center py-10 text-gray-500">
        No published or scheduled blogs found.
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full table-fixed">
        <thead>
          <tr className="bg-gray-50 border-b">
            <th className="text-left p-4 font-medium w-[40%]">Name</th>
            <th className="text-left p-4 font-medium w-[20%]">Date Created</th>
            <th className="text-left p-4 font-medium w-[25%]">Publish Date</th>
            <th className="text-right p-4 font-medium w-[15%]">Actions</th>
          </tr>
        </thead>
        <tbody>
          {blogs.map((blog) => (
            <tr key={blog.id} className="border-b hover:bg-gray-50">
              <td className="p-4">
                <div className="flex items-center gap-2">
                  <input type="checkbox" className="rounded border-gray-300" />
                  <span>{blog.title}</span>
                </div>
              </td>
              <td className="p-4 text-gray-600">
                {new Date(blog.created_at).toLocaleDateString('en-US', {
                  weekday: 'short',
                  day: '2-digit',
                  month: 'short',
                  year: '2-digit',
                })}
              </td>
              <td className="p-4">
                <div className="flex items-center gap-2">
                  <span>
                    {blog.scheduled_at
                      ? new Date(blog.scheduled_at).toLocaleDateString(
                          'en-US',
                          {
                            weekday: 'short',
                            day: '2-digit',
                            month: 'short',
                            year: '2-digit',
                          }
                        )
                      : 'N/A'}
                  </span>
                  <span
                    className={`px-2 py-1 rounded-md text-xs ${getStatusColor(blog.status)}`}
                  >
                    {blog.status.charAt(0).toUpperCase() + blog.status.slice(1)}
                  </span>
                </div>
              </td>
              <td className="p-4">
                <div className="flex justify-end gap-2">
                  {handleDownloadFile && (
                    <button
                      className="p-1 text-gray-400 hover:text-[#846CEC]"
                      onClick={() =>
                        handleDownloadFile(
                          blog.id,
                          blog.title || blog.file_name
                        )
                      }
                      title="Print / Export"
                    >
                      <Download size={18} />
                    </button>
                  )}
                  <button
                    className="p-1 text-gray-400 hover:text-[#846CEC]"
                    onClick={() => handleDeleteBlog(blog.id)}
                  >
                    <X size={18} />
                  </button>
                  {blog.status === 'draft' && handlePublishBlog && (
                    <button
                      className="px-2 py-1 text-xs bg-green-100 text-green-600 rounded-md"
                      onClick={() => handlePublishBlog(blog.id)}
                    >
                      Publish
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PublishedBlogsTable;
